import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { parseBBCode } from '../../../../libraries/BBCode'
import { buildCharacterSeals } from '../utils/sealUtils'

export default function CharacterPortraitPanel({ char = {}, slug = '', expPct = 0 }) {
  const shuriken = '/assets/images/shuriken.png'
  const slugCandidate = slug ? `/assets/images/${slug}.png` : ''
  const primary = (char?.portrait && String(char.portrait).trim()) || ''
  const initialSrc = primary || slugCandidate || shuriken
  const [src, setSrc] = useState(initialSrc)
  const handleImgError = () => {
    if (src !== shuriken) setSrc(shuriken)
  }

  const givenName = String(char?.name || 'Sin nombre')
  const clanLabel = String(char?.clan || '').trim()
  const fullName = clanLabel ? `${clanLabel} ${givenName}` : givenName
  const level = Number(char?.level || 1)
  const pct = Math.max(0, Math.min(100, Number(expPct || 0)))
  const expCurrent = Number(char?.experience?.current || 0)
  const expMax = Number(char?.experience?.max || 0)
  const isAcademy = Boolean(char?.academyMode)
  const alignmentRaw = String(char?.alignment || '').trim()
  // Academia: chakra/naturaleza aún no despertados → «Latente» en lugar de «Sin definir».
  const alignment = alignmentRaw || (isAcademy ? 'Latente' : 'Sin definir')
  const gender = String(char?.gender || '').trim()
  // Academia: ocultar Shinobi/Kunoichi (default del wizard confunde con el camino).
  const showGender =
    Boolean(gender) &&
    !(isAcademy && /^(shinobi|kunoichi)$/i.test(gender))

  const bioHtml = useMemo(() => {
    const bio = String(
      char?.descPsychological || char?.bio || char?.biography || char?.description || ''
    ).trim()
    if (!bio) return null
    return parseBBCode(bio)
  }, [char?.descPsychological, char?.bio, char?.biography, char?.description])

  const seals = useMemo(() => buildCharacterSeals(char), [char])

  const nameWords = useMemo(() => {
    const words = []
    if (clanLabel) words.push(clanLabel)
    givenName
      .split(/\s+/)
      .map((w) => w.trim())
      .filter(Boolean)
      .forEach((w) => words.push(w))
    return words.length > 0 ? words : ['Sin nombre']
  }, [clanLabel, givenName])

  return (
    <aside className="char-left-panel sticky" aria-label="Retrato y biografía">
      <div className="char-left-card">
        <div className="char-left-level">
          <div className="char-level-block char-level-block--top">
            <div className="char-level-block__header">
              <span className="char-level-block__label">Nivel</span>
              <span className="char-level-block__value">{level}</span>
            </div>
            <div className="char-expbar char-expbar--diagonal" aria-label={`Experiencia ${expCurrent} de ${expMax}`}>
              <div className="char-expbar__fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="char-level-block__xp">
              <span>{expCurrent.toLocaleString()}</span>
              <span>{expMax.toLocaleString()} XP</span>
            </div>
          </div>
        </div>

        <div className="char-left-identity char-left-identity--stacked">
          <h2 className="char-name-title" aria-label={fullName}>
            {nameWords.map((word, i) => (
              <span key={`name-part-${i}`} className="char-name-title__word">
                {word}
              </span>
            ))}
          </h2>

          <div className="char-identity-meta">
            {char?.rank || char?.effectiveRankLabel ? (
              <span className="char-meta-chip char-meta-chip--rank">
                {char.effectiveRankLabel || char.rank}
              </span>
            ) : null}
            {showGender ? <span className="char-meta-chip">{gender}</span> : null}
            <span className="char-meta-chip char-meta-chip--alignment">{alignment}</span>
          </div>
        </div>

        <div className="char-left-portrait-block">
          <div className="char-portrait-wrap">
            <div className="char-portrait-frame" aria-label="Retrato del personaje">
              <div className="char-portrait-frame__inner">
                <img src={src} alt={fullName} loading="lazy" onError={handleImgError} width="150" height="200" />
                <div className="char-portrait-frame__vignette" aria-hidden="true" />
              </div>
              <span className="char-portrait-corner char-portrait-corner--tl" aria-hidden="true" />
              <span className="char-portrait-corner char-portrait-corner--tr" aria-hidden="true" />
              <span className="char-portrait-corner char-portrait-corner--bl" aria-hidden="true" />
              <span className="char-portrait-corner char-portrait-corner--br" aria-hidden="true" />
            </div>
          </div>

          {seals.length > 0 ? (
            <div className="char-seals-row" aria-label="Sellos del personaje">
              {seals.map((seal, index) => (
                <div
                  key={seal.key}
                  className={`char-seal char-seal--${seal.variant}`}
                  style={{ '--seal-rotate': `${-10 + index * 7}deg` }}
                  role="img"
                  aria-label={seal.title}
                  title={seal.title}
                >
                  {seal.text}
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <section className="char-left-story" aria-label="Biografía">
          <blockquote className="char-bio">
            <span className="char-bio__quote" aria-hidden="true">
              "
            </span>
            {bioHtml ? (
              <div className="char-bio__content" dangerouslySetInnerHTML={{ __html: bioHtml }} />
            ) : (
              <span className="char-bio__empty">Sin biografía registrada.</span>
            )}
          </blockquote>
          <Link to={`/personajes/${slug}/historia`} className="char-bio__more">
            Ver biografía completa
          </Link>
        </section>
      </div>
    </aside>
  )
}
